/**
 * Drizzle query builders + query keys for FORUMS, ANNOUNCEMENTS, NOTIFICATIONS
 * and BADGES (F3-C). Local-first consumption: data arrives via the course
 * streams (forum), by cohort (forum_thread/forum_post/announcement) and
 * private ones (my_notifications/my_badges). Writes go through the
 * write-path (moderation authz / read_at-only for notifications; badge_award is server-only).
 */

import {
  and,
  announcement,
  asc,
  badge_award,
  cohort,
  cohort_enrollment,
  cohort_teacher,
  course,
  desc,
  eq,
  forum,
  forum_post,
  forum_thread,
  isNull,
  notification,
  or,
  type PowerSyncDrizzleDB,
} from "@space/powersync";

export const communityQueryKeys = {
  all: ["academy-community"] as const,
  forumByModule: (moduleId: string | undefined) =>
    ["academy-community", "forum", "module", moduleId] as const,
  threads: (forumId: string | undefined, cohortId: string | null | undefined) =>
    ["academy-community", "threads", forumId, cohortId ?? "course-wide"] as const,
  posts: (threadId: string | undefined) => ["academy-community", "posts", threadId] as const,
  notifications: (userId: string | undefined) =>
    ["academy-community", "notifications", userId] as const,
  unread: (userId: string | undefined) =>
    ["academy-community", "notifications", "unread", userId] as const,
  badges: (userId: string | undefined) => ["academy-community", "badges", userId] as const,
  announcementsByOrg: (orgId: string | undefined) =>
    ["academy-community", "announcements", "org", orgId] as const,
  announcementsByCohort: (cohortId: string | undefined) =>
    ["academy-community", "announcements", "cohort", cohortId] as const,
  studentCohort: (courseId: string | undefined, userId: string | undefined) =>
    ["academy-community", "student-cohort", courseId, userId] as const,
  teachingCohort: (courseId: string | undefined, userId: string | undefined) =>
    ["academy-community", "teaching-cohort", courseId, userId] as const,
  courseAuthor: (courseId: string | undefined, userId: string | undefined) =>
    ["academy-community", "course-author", courseId, userId] as const,
};

// ─── Viewer's cohort context for a course ─────────────────────────────

/** Cohort context row (id + org) for the forum. */
export interface ForumCohortContext {
  cohort_id: string;
  organization_id: string | null;
}

/** ACTIVE cohort of the STUDENT for a course (if enrolled). */
export function getMyStudentCohortForCourseQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  courseId: string,
) {
  return db
    .select({ cohort_id: cohort.id, organization_id: cohort.organization_id })
    .from(cohort_enrollment)
    .innerJoin(cohort, eq(cohort.id, cohort_enrollment.cohort_id))
    .where(
      and(
        eq(cohort_enrollment.user_id, userId),
        eq(cohort_enrollment.status, "active"),
        isNull(cohort_enrollment.deleted_at),
        eq(cohort.course_id, courseId),
        isNull(cohort.deleted_at),
      ),
    )
    .limit(1);
}

/** Cohort I TEACH for a course (if I'm a teacher). */
export function getMyTeachingCohortForCourseQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  courseId: string,
) {
  return db
    .select({ cohort_id: cohort.id, organization_id: cohort.organization_id })
    .from(cohort_teacher)
    .innerJoin(cohort, eq(cohort.id, cohort_teacher.cohort_id))
    .where(
      and(
        eq(cohort_teacher.user_id, userId),
        eq(cohort.course_id, courseId),
        isNull(cohort.deleted_at),
      ),
    )
    .limit(1);
}

/** Am I the AUTHOR of the course? (authorship → course-wide forum moderation). */
export function getAmICourseAuthorQuery(db: PowerSyncDrizzleDB, userId: string, courseId: string) {
  return db
    .select({ id: course.id })
    .from(course)
    .where(and(eq(course.id, courseId), eq(course.author_id, userId), isNull(course.deleted_at)))
    .limit(1);
}

// ─── Forum ─────────────────────────────────────────────────────────────────────

/** `forum` row of a module (1:1). id = `forum-<moduleId>`. */
export function getForumByModuleQuery(db: PowerSyncDrizzleDB, moduleId: string) {
  return db
    .select()
    .from(forum)
    .where(and(eq(forum.module_id, moduleId), isNull(forum.deleted_at)))
    .limit(1);
}

/**
 * Threads visible in the CONTEXT of a cohort: that cohort's PLUS the
 * course-wide ones (cohort_id NULL). Pinned first, then by recency.
 */
export function getForumThreadsQuery(
  db: PowerSyncDrizzleDB,
  forumId: string,
  cohortId: string | null,
) {
  const scope = cohortId
    ? or(eq(forum_thread.cohort_id, cohortId), isNull(forum_thread.cohort_id))
    : isNull(forum_thread.cohort_id);
  return db
    .select()
    .from(forum_thread)
    .where(and(eq(forum_thread.forum_id, forumId), isNull(forum_thread.deleted_at), scope))
    .orderBy(desc(forum_thread.pinned), desc(forum_thread.created_at));
}

/** Posts of a thread (not deleted), in chronological order. Hidden ones reach
 * the teacher (moderation); the student doesn't receive them (filtered in the stream). */
export function getThreadPostsQuery(db: PowerSyncDrizzleDB, threadId: string) {
  return db
    .select()
    .from(forum_post)
    .where(and(eq(forum_post.thread_id, threadId), isNull(forum_post.deleted_at)))
    .orderBy(asc(forum_post.created_at));
}

// ─── Notifications ───────────────────────────────────────────────────────────

/** MY notifications (most recent first). */
export function getMyNotificationsQuery(db: PowerSyncDrizzleDB, userId: string, limit = 100) {
  return db
    .select()
    .from(notification)
    .where(eq(notification.user_id, userId))
    .orderBy(desc(notification.created_at))
    .limit(limit);
}

/** MY UNREAD notifications (for the bell counter). */
export function getUnreadNotificationsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(notification)
    .where(and(eq(notification.user_id, userId), isNull(notification.read_at)));
}

// ─── Badges ─────────────────────────────────────────────────────────────────

/** MY badges awarded (most recent first). */
export function getMyBadgesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(badge_award)
    .where(eq(badge_award.user_id, userId))
    .orderBy(desc(badge_award.awarded_at));
}

// ─── Announcements ─────────────────────────────────────────────────────────────

/**
 * ORG/CAMPUS-scoped announcements (cohort_id NULL) of an organization.
 *
 * D9: the `publish_at` embargo is NOT filtered here — embargoed ones no
 * longer arrive. Publication is materialized by the server in
 * `announcement.is_published` and the stream filters by that column, so a
 * future-scheduled announcement only sits on the device of whoever drafted it
 * or manages the academy (exactly who needs to see it, marked as scheduled in the view).
 */
export function getAnnouncementsByOrgQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(announcement)
    .where(
      and(
        eq(announcement.organization_id, orgId),
        isNull(announcement.cohort_id),
        isNull(announcement.deleted_at),
      ),
    )
    .orderBy(desc(announcement.pinned), desc(announcement.publish_at));
}

/** Announcements targeted at a cohort. */
export function getAnnouncementsByCohortQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(announcement)
    .where(and(eq(announcement.cohort_id, cohortId), isNull(announcement.deleted_at)))
    .orderBy(desc(announcement.pinned), desc(announcement.publish_at));
}
